/**
 * 需要想定値の上限と負の値 (spec demand-forecast-common.md AC-DFC-EDIT-004 /
 * AC-DFC-EDIT-005 / AC-DFC-006d) の判定と文言の組み立て。
 *
 * - 上限 999,999,999 kWh/30min は 1 コマの値と BG 全体の合計の両方に適用する。
 *   値は供給力 48 点データと共有する `MAX_STORED_SLOT_VALUE` (OCCTO 数量型 I9)。
 * - 判定は GC 以降のコマ (`koma > gcKoma`) だけを対象にする。GC 済みコマは編集
 *   できず、超過が残ると直す手段のない拒否が続くため。
 * - 保存前の画面判定 (executeSaveAll) と確定時の backend エラー表示
 *   (serverError.ts) が同じ文言・整形を使うよう、文言のパラメータ組み立てを
 *   `limitMessageParams` に集約する。
 */
import type { TFunction } from 'i18next'

import { komaNumberToTime } from '~/models/komaToTime'
import {
  MAX_SHOWN_REJECTIONS,
  MAX_STORED_SLOT_VALUE,
} from '~/models/komaValueLimit'

import { bgTotalBreakdownSlots, numberToPlainString } from './slotHelpers'
import type { KomaSlots } from './types'

/**
 * 上限を超えた値。`value` は文言にそのまま出す 10 進表記 (指数表記なし・丸めなし) で、
 * BG 全体の合計は number に戻すと有効桁を超えて丸まるため文字列で持つ。
 */
export type SlotValueLimitViolation =
  | { kind: 'slot'; koma: number; value: string; retailerBusinessCd: string }
  | { kind: 'bgTotal'; koma: number; value: string }

/** GC 以降のコマで上限を超える値を、コマ順に返す (1 本の系列に対する走査)。 */
function overLimitKomas(
  slots: KomaSlots,
  gcKoma: number,
): Array<{ koma: number; value: string }> {
  const over: Array<{ koma: number; value: string }> = []
  for (const [index, value] of slots.entries()) {
    const koma = index + 1
    if (koma > gcKoma && value !== undefined && value > MAX_STORED_SLOT_VALUE) {
      over.push({ koma, value: numberToPlainString(value) })
    }
  }
  return over
}

/**
 * number を、最短の 10 進表記の数字列 (整数) と小数桁数に分ける。指数表記の展開は
 * 1 コマの値の表示と同じ `numberToPlainString` に任せる。
 * 例: 999999998.6 → { digits: 9999999986, scale: 1 }、1.5e-7 → { digits: 15, scale: 8 }。
 */
function toScaledDecimal(value: number): { digits: bigint; scale: number } {
  const [integer, fraction = ''] = numberToPlainString(value).split('.')
  return { digits: BigInt(integer + fraction), scale: fraction.length }
}

/**
 * BG 全体の合計が上限を超えるかを、値の 10 進表記で誤差なく判定する。number の加算は
 * 2 進の丸め誤差を持ち (999999998.6 + 0.2 + 0.2 = 999999999.0000001)、上限ちょうどの
 * 合計を超過と誤判定するため。backend も合計を 10 進 (decimal) で求めている。
 * 超える時は合計値 (丸めない 10 進表記)、超えない時は undefined を返す。
 */
function bgTotalOverLimit(values: number[]): string | undefined {
  const parts = values.map(toScaledDecimal)
  const scale = Math.max(0, ...parts.map((p) => p.scale))
  const total = parts.reduce(
    (sum, p) => sum + p.digits * BigInt(10) ** BigInt(scale - p.scale),
    BigInt(0),
  )
  const limit = BigInt(MAX_STORED_SLOT_VALUE) * BigInt(10) ** BigInt(scale)
  if (total <= limit) return undefined
  const text = total.toString().padStart(scale + 1, '0')
  if (scale === 0) return text
  // 小数部の末尾の 0 は値を変えないので落とす (0.5 + 0.5 の合計を 1.0 と出さない)
  const fraction = text.slice(text.length - scale).replace(/0+$/, '')
  const integer = text.slice(0, text.length - scale)
  return fraction === '' ? integer : `${integer}.${fraction}`
}

/** GC 以降のコマで、BG 全体の合計が上限を超えるコマをコマ順に返す (未入力は数えない)。 */
function bgTotalOverLimitKomas(
  slotsList: KomaSlots[],
  gcKoma: number,
): Array<{ koma: number; value: string }> {
  const over: Array<{ koma: number; value: string }> = []
  for (let index = gcKoma; index < 48; index++) {
    const values = slotsList
      .map((slots) => slots[index])
      .filter((v): v is number => v !== undefined)
    if (values.length === 0) continue
    const total = bgTotalOverLimit(values)
    if (total !== undefined) over.push({ koma: index + 1, value: total })
  }
  return over
}

/**
 * GC 以降のコマで上限を超える値を、コマ順に列挙する。事業者ごとの系列と、その合計
 * (BG 全体。10 進で誤差なく合計する) の系列に同じ走査をかける。同じコマでは小売電気事業者
 * 別の超過を先に、BG 全体の合計の超過を後に並べる。
 *
 * @param slotsByRetailer 画面の全事業者の編集中の値 (保存後の状態)
 * @param gcKoma GC 済みコマ数 (koma <= gcKoma は判定しない)
 */
export function findSlotValueLimitViolations(
  slotsByRetailer: Record<string, KomaSlots>,
  gcKoma: number,
  unattributedSlots?: KomaSlots,
): SlotValueLimitViolation[] {
  const entries = Object.entries(slotsByRetailer)
  const violations: SlotValueLimitViolation[] = [
    ...entries.flatMap(([retailerBusinessCd, slots]) =>
      overLimitKomas(slots, gcKoma).map(
        ({ koma, value }): SlotValueLimitViolation => ({
          kind: 'slot',
          koma,
          value,
          retailerBusinessCd,
        }),
      ),
    ),
    ...bgTotalOverLimitKomas(
      bgTotalBreakdownSlots(
        entries.map(([, slots]) => slots),
        unattributedSlots,
      ),
      gcKoma,
    ).map(
      ({ koma, value }): SlotValueLimitViolation => ({
        kind: 'bgTotal',
        koma,
        value,
      }),
    ),
  ]
  // Array.prototype.sort は安定なので、同じコマでは上の並び (事業者別 → 合計) が残る。
  return violations.sort((a, b) => a.koma - b.koma)
}

/** GC 以降のコマで負の値を持つコマ番号を、コマ順・重複なしで返す。 */
export function collectNegativeKomas(
  slotsList: KomaSlots[],
  gcKoma: number,
): number[] {
  const komas = new Set<number>()
  for (const slots of slotsList) {
    for (const [index, value] of slots.entries()) {
      const koma = index + 1
      if (koma > gcKoma && value !== undefined && value < 0) komas.add(koma)
    }
  }
  return [...komas].sort((a, b) => a - b)
}

/**
 * 値を丸めずに桁区切りだけ付ける。小数は切り捨て・四捨五入をしない
 * (999,999,999.004 が「999,999,999」と出て上限以内に見えるのを防ぐ)。
 * `raw` は backend が返す整形しない decimal 文字列、または画面判定の違反値 (指数表記なし)。
 */
function formatExactValue(raw: string): string {
  const [integer, fraction] = raw.split('.')
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return fraction === undefined ? grouped : `${grouped}.${fraction}`
}

/** 上限超過の文言に埋め込む値 (桁区切りの値・コマの時刻表記・対象日)。 */
export function limitMessageParams(
  params: { koma: number; value: string },
  dateLabel: string,
): { date: string; koma: string; value: string } {
  return {
    date: dateLabel,
    koma: komaNumberToTime(params.koma),
    value: formatExactValue(params.value),
  }
}

/** 上限超過の reason / 種別ごとの i18n key (保存前の画面判定と確定エラーで共有する)。 */
export const LIMIT_MESSAGE_KEYS = {
  slot: 'page:demandForecastDaily.valueLimit.slotOver',
  bgTotal: 'page:demandForecastDaily.valueLimit.bgTotalOver',
} as const

/** 保存前の画面判定で見つけた超過を 1 件の文言にする。 */
function formatLimitViolation(
  violation: SlotValueLimitViolation,
  t: TFunction,
  dateLabel: string,
): string {
  return t(
    LIMIT_MESSAGE_KEYS[violation.kind],
    limitMessageParams(
      { koma: violation.koma, value: violation.value },
      dateLabel,
    ),
  )
}

/** 超過の一覧を、先頭数件 + 「ほか N 件」の行にまとめる。 */
export function formatLimitViolationLines(
  violations: SlotValueLimitViolation[],
  t: TFunction,
  dateLabel: string,
): string[] {
  const lines = violations
    .slice(0, MAX_SHOWN_REJECTIONS)
    .map((violation) => formatLimitViolation(violation, t, dateLabel))
  if (violations.length > MAX_SHOWN_REJECTIONS) {
    lines.push(
      t('page:demandForecastDaily.valueLimit.more', {
        count: violations.length - MAX_SHOWN_REJECTIONS,
      }),
    )
  }
  return lines
}

/** 負の値のコマの警告文 (AC-DFC-EDIT-005)。複数コマは区切り文字で並べる。 */
export function formatNegativeWarning(
  komas: number[],
  t: TFunction,
  dateLabel: string,
): string {
  return t('page:demandForecastDaily.valueLimit.negativeWarning', {
    slots: komas
      .map((koma) => `${dateLabel} ${komaNumberToTime(koma)}`)
      .join(t('page:demandForecastDaily.valueLimit.listSeparator')),
  })
}
